import {nanoid} from 'nanoid'
import {useMemo, useRef} from 'react'

import {IconInfoCircle} from '@sdox/icons'
import {twMerge} from '@sdox/utils'

import {Tooltip} from '../Tooltip'
import {Media} from '../Media'

import type {UiMediaRef} from '../../types'
import type {ComponentProps} from 'react'
import type {TooltipRefProps} from 'react-tooltip'

export interface AwardItemProps extends ComponentProps<'div'> {
  text?: string
  awardImage: UiMediaRef
}

export const AwardItem = (props: AwardItemProps) => {
  const {
    title,
    className,
    text,
    awardImage,
    ...restProps
  } = props

  const tooltipRef = useRef<TooltipRefProps>(null)
  const id = useMemo(() => `award_${nanoid(10)}`, [])

  return (
    <div
      className={twMerge(
        'relative flex flex-col',
        'aspect-[1/1] rounded-radius-md border-1 border-outline-secondary p-spacing-40',
        className,
      )}
      {...restProps}
    >
      {text ? (
        <Tooltip
          ref={tooltipRef}
          title={title}
          body={text}
          position={'top-end'}
          anchorSelect={`#${id}`}
        >
          <div
            id={id}
            className={'group absolute bottom-spacing-12 right-spacing-12'}
            onMouseEnter={() => tooltipRef.current?.open()}
            onMouseLeave={() => tooltipRef.current?.close()}
          >
            <IconInfoCircle className={'text-glyph-default-secondary group-hover:text-glyph-default-primary'} />
          </div>
        </Tooltip>
      ) : null}
      {/* aspect ratio wrapper added for better compatibility with safari*/}
      <div className={'aspect-1'}>
        <Media
          src={awardImage.thumbnailUrl}
          placeholderSrc={awardImage.thumbnailUrl380w}
          alt={title ?? 'award'}
          aspectRatio={'3/2'}
          imageClassName={'object-contain'}
          width={awardImage.originalThumbnailDimensions?.width}
          height={awardImage.originalThumbnailDimensions?.height}
          hasBackground={false}
        />
      </div>
    </div>
  )
}
